import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  BellRing,
  CheckCircle2,
  LogOut,
  Stethoscope,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { PublicQueueView } from '@rx-manager/shared';
import { BookingApiError, checkInApi } from '../../lib/api';
import { useI18n, type Translate } from '../../lib/i18n';
import { Alert, Button, Card, HEADING, Spinner, cn } from '../../components/ui';
import { useShellBrand } from '../../components/Shell';
import { FormsDue } from '../forms/FormsDue';
import { aheadText, checkInFailure, failureText, LinkProblem, TicketNumbers, ticketsOf, type Failure } from './shared';

/**
 * Track CIQ (CIQ-D16) — the patient's own place in line (`/q/<token>`): their number, how many are
 * ahead (never minutes), "It's your turn" when they are called, the forms still due for the visit,
 * and Leave the line. Nothing about anyone else, and nothing personal: whoever holds the link reads
 * the same page. The link ends with the place's day.
 *
 * It looks again every 15 seconds while they are in line, and asks for the forms on the first look
 * and every fourth after (they change rarely; the line often).
 */

const POLL_MS = 15_000;
const FORMS_EVERY = 4;
/** The line is over for them: nothing more to look at. */
const CLOSED = new Set<PublicQueueView['state']>(['done', 'left']);

type Load = 'loading' | 'ready' | 'missing' | 'offline' | 'error';

export function QueuePage() {
  const { token = '' } = useParams();
  const { t } = useI18n();
  const [view, setView] = useState<PublicQueueView | null>(null);
  const [load, setLoad] = useState<Load>('loading');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // The band names the place once the link resolves.
  useShellBrand(view ? { name: view.place.name } : null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let looks = 0;
    const look = async (): Promise<void> => {
      const withForms = looks % FORMS_EVERY === 0;
      looks += 1;
      try {
        const next = await checkInApi.queue(token, withForms);
        if (cancelled) return;
        // A look without the forms keeps the last list the server gave.
        setView((current) => (withForms ? next : { ...next, forms: current?.forms }));
        setLoad('ready');
        if (CLOSED.has(next.state)) return;
      } catch (error) {
        if (cancelled) return;
        if (error instanceof BookingApiError && error.isMissing) {
          setLoad('missing');
          return;
        }
        // A look that failed keeps what is on screen, and tries again.
        const offline = error instanceof BookingApiError && error.status === 0;
        setLoad((current) => (current === 'ready' ? current : offline ? 'offline' : 'error'));
      }
      timer = setTimeout(() => void look(), POLL_MS);
    };
    void look();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [token]);

  useEffect(() => {
    if (load === 'ready') headingRef.current?.focus();
  }, [load]);

  const leave = async (): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      const next = await checkInApi.leave(token);
      setView((current) => ({ ...next, forms: current?.forms }));
      setConfirming(false);
    } catch (error) {
      if (error instanceof BookingApiError && error.isMissing) {
        setLoad('missing');
        return;
      }
      setFailure(checkInFailure(error));
    } finally {
      setBusy(false);
    }
  };

  if (load === 'loading') return <Spinner label={t('queue.loading')} />;
  if (load === 'missing') return <LinkProblem message="queue.missing" />;
  if (!view) return <Alert>{t(load === 'offline' ? 'error.offline' : 'error.unknown')}</Alert>;

  const open = !CLOSED.has(view.state);
  const leavable = view.state === 'waiting' || view.state === 'called';
  // A state this page doesn't know yet (a newer server) reads as out of the line.
  const line = STATE_LINE[view.state] ?? STATE_LINE.left;
  const Icon = line.icon;

  return (
    <div className="space-y-6">
      <h1 ref={headingRef} tabIndex={-1} className={HEADING}>
        {t('queue.heading')}
      </h1>
      {failure && <Alert>{failureText(failure, t)}</Alert>}

      <div className="rounded-xl border border-border bg-card p-5 text-center shadow-sm">
        <TicketNumbers tickets={ticketsOf(view)} />
      </div>

      {/* The one live line: it changes as the line moves. */}
      <p
        role="status"
        className={cn(
          'flex items-start gap-3 rounded-xl border p-4 font-medium',
          line.box,
          view.state === 'called' ? 'text-xl sm:text-2xl' : 'text-lg',
        )}
      >
        <Icon className={cn('mt-0.5 h-6 w-6 shrink-0', line.iconClass)} aria-hidden="true" />
        {line.text(view, t)}
      </p>

      {/* Track SFM — the forms still due for the visit, with their links (as the manage page). */}
      {open && view.forms && view.forms.length > 0 && <FormsDue forms={view.forms} />}

      {leavable &&
        (confirming ? (
          <Card tone="muted" className="space-y-4">
            <p className="text-base font-medium text-heading">{t('queue.leaveQuestion')}</p>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => setConfirming(false)} disabled={busy}>
                {t('queue.stay')}
              </Button>
              <Button variant="danger" onClick={() => void leave()} loading={busy}>
                {t('queue.leaveConfirm')}
              </Button>
            </div>
          </Card>
        ) : (
          <Button variant="outline" onClick={() => setConfirming(true)}>
            <LogOut className="h-4 w-4" aria-hidden="true" />
            {t('queue.leave')}
          </Button>
        ))}

      {open && <p className="text-sm text-muted-foreground">{t('queue.updates')}</p>}
    </div>
  );
}

/** Each state in words, with an icon, never colour alone. */
const STATE_LINE: Record<
  PublicQueueView['state'],
  { text: (view: PublicQueueView, t: Translate) => string; icon: LucideIcon; box: string; iconClass: string }
> = {
  waiting: {
    text: (view, t) => aheadText(view.ahead ?? 0, t),
    icon: Users,
    box: 'border-primary/25 bg-accent text-heading',
    iconClass: 'text-primary-strong',
  },
  called: {
    text: (_view, t) => t('queue.called'),
    icon: BellRing,
    box: 'border-primary bg-primary text-primary-foreground shadow-sm',
    iconClass: 'text-primary-foreground',
  },
  serving: {
    text: (_view, t) => t('queue.serving'),
    icon: Stethoscope,
    box: 'border-success/30 bg-success/5 text-heading',
    iconClass: 'text-success',
  },
  done: {
    text: (_view, t) => t('queue.done'),
    icon: CheckCircle2,
    box: 'border-success/30 bg-success/5 text-heading',
    iconClass: 'text-success',
  },
  left: {
    text: (_view, t) => t('queue.left'),
    icon: LogOut,
    box: 'border-border bg-muted/60 text-heading',
    iconClass: 'text-muted-foreground',
  },
};
